import React from 'react';
import { useTranslation } from 'react-i18next';

interface OrderData {
  game: {
    name: string;
    image: string;
  };
  product: string;
  price: number;
  phoneNumber: string;
  userData: Record<string, any>;
  payment: string;
  createdAt?: string;
}

interface OrderSummaryProps {
  orderData: OrderData;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ orderData }) => {
  const { t } = useTranslation('order');

  // VocaGame's user_input field names map to a small set of recognised
  // labels — translate via i18n. Unknown keys fall through to the raw
  // key string so a new field type is at least readable.
  const fieldLabel = (key: string): string => {
    if (key === 'userId') return t('field_player_id');
    if (key === 'zoneId' || key === 'server') return t('field_server');
    if (key === 'phoneNumber') return t('phone_number');
    return key;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
      {/* Title */}
      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-6">
        {t('info_title')}
      </h2>

      {/* Game Product Row */}
      <div className="flex items-center gap-4 mb-6">
        {/* Game Image */}
        {orderData.game.image ? (
          <img
            src={orderData.game.image}
            alt={orderData.game.name}
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-gray-200 flex-shrink-0" />
        )}

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800 truncate">
            {orderData.product}
          </h3>
          <p className="text-sm text-gray-600">{orderData.game.name}</p>
        </div>

        {/* Price */}
        <div className="text-right">
          <p className="font-bold text-primary">
            ${orderData.price.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 mb-6"></div>

      {/* Order Details */}
      <div className="space-y-4 mb-6">
        {/* Phone Number */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">{t('phone_number')}</span>
          <span className="text-sm font-medium text-gray-900">
            {orderData.phoneNumber || '-'}
          </span>
        </div>

        {/* Dynamic user_data fields */}
        {Object.entries(orderData.userData || {}).map(([key, value]) => (
          <div key={key} className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {fieldLabel(key)}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {String(value) || '-'}
            </span>
          </div>
        ))}

        {/* Payment */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">{t('payment_label')}</span>
          <span className="text-sm font-medium text-gray-900">
            {orderData.payment}
          </span>
        </div>

        {/* Date */}
        {orderData.createdAt && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{t('date_label')}</span>
            <span className="text-sm font-medium text-gray-900">
              {(() => {
                const d = new Date(orderData.createdAt);
                const offset = -d.getTimezoneOffset();
                const h = Math.floor(Math.abs(offset) / 60);
                const m = Math.abs(offset) % 60;
                const sign = offset >= 0 ? '+' : '-';
                const gmt = m > 0 ? `GMT${sign}${h}:${String(m).padStart(2, '0')}` : `GMT${sign}${h}`;
                return d.toLocaleString('en-US', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                }) + ` (${gmt})`;
              })()}
            </span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 mb-4"></div>

      {/* Total */}
      <div className="flex justify-between items-center">
        <span className="text-base font-bold text-gray-900">{t('total_label')}</span>
        <span className="text-xl font-bold text-primary">
          USD {orderData.price.toFixed(2)}
        </span>
      </div>
    </div>
  );
};

export default OrderSummary;
